import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { enumReadSchema } from "../../../../shared/core/enum-read.schema";
import {
  detectVibeEnvVarSecretMaterial,
  VIBE_ENV_VAR_SECRET_MATERIAL_KINDS,
  type VibeEnvVarSecretMaterialFinding,
  vibeEnvVarSecretMaterialMessage
} from "../../../../shared/domain/vibe/env-var-secret-material";

/**
 * The Prisma enum `VibeEnvVarScope`, from the generated `DbEnum` tuple. That
 * module is plain string tuples, so it carries none of Prisma's runtime into the
 * frontend bundle, unlike `z.nativeEnum($Enums.VibeEnvVarScope)`.
 */
export const VibeEnvVarScopeSchema = z.enum(DbEnum.VIBE_ENV_VAR_SCOPE_VALUES);

/** The scope as a reader parses it: a scope this build does not list reads as unlisted. */
export const VibeEnvVarScopeReadSchema = enumReadSchema(DbEnum.VIBE_ENV_VAR_SCOPE_VALUES);
export type VibeEnvVarScopeValue = z.infer<typeof VibeEnvVarScopeSchema>;

/**
 * POSIX-shape env var name. Must start with a letter or underscore;
 * followed by uppercase letters, digits, or underscores. We reject
 * lowercase to surface accidental mistakes (`api_key=...` vs `API_KEY=...`)
 * before they reach the build runner — the convention is universal
 * across `process.env` consumers.
 */
export const VibeEnvVarNameSchema = z
  .string()
  .trim()
  .min(1, "name is required")
  .max(255, "name must be ≤ 255 characters")
  .regex(/^[A-Z_][A-Z0-9_]*$/, "name must be SCREAMING_SNAKE_CASE (A-Z, 0-9, underscore)");

/**
 * Env var value AS STORED. We accept any string up to a generous cap —
 * newlines, URLs, JSON blobs are all valid. Empty is allowed (some apps
 * treat an empty env var as "feature off").
 *
 * This is the READ shape and it deliberately carries no secret-material
 * check. Rows written before that check existed are exactly the ones an
 * operator needs to LIST in order to find and delete them, so a refusal
 * here would break the only endpoint that can clean them up — and it
 * would break it with a 500 on the response envelope rather than a
 * message anyone could act on. Use `VibeEnvVarSettableValueSchema` on
 * every write path instead.
 */
export const VibeEnvVarValueSchema = z.string().max(32_768, "value must be ≤ 32 KB");

/**
 * Env var value AS SUBMITTED. The read shape plus the refusal of private
 * key material.
 *
 * Env vars are stored and served in plaintext: `GET /api/vibe/apps/:appId/env`
 * returns `value` verbatim to any caller holding an org API key with
 * `vibe:read`. That is the documented contract for CONFIGURATION, and it is
 * why key material must take the other door — a `VibeAppSecretRef` plus a
 * staged value, sealed under the tenant's own KMS CMK and delivered to the
 * cluster's Vault by its agent.
 *
 * The same predicate backs `VibeAppEnvVar.create` in the backend domain, so
 * the HTTP boundary and the domain enforce ONE rule that cannot drift — the
 * arrangement `VibeAppNameSchema` already has with `VibeApp.create`.
 */
export const VibeEnvVarSettableValueSchema = VibeEnvVarValueSchema.superRefine((value, ctx) => {
  const finding = detectVibeEnvVarSecretMaterial(value);
  if (finding === null) return;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: vibeEnvVarSecretMaterialMessage(finding)
  });
});

/**
 * The read side of the same gate — what `detectVibeEnvVarSecretMaterial` makes
 * of a value that is ALREADY STORED.
 *
 * `VibeEnvVarSettableValueSchema` above closed the WRITE path; it says nothing
 * about rows written before it existed, and those are the ones an operator has
 * to find in order to delete them. This is the shape that lets them be found.
 *
 * ## It is the domain finding, not a copy of it
 *
 * The `satisfies` below pins one direction — a field added to
 * `VibeEnvVarSecretMaterialFinding` and not declared here stops compiling.
 * It cannot pin the other, because Zod 4 declares both type parameters of
 * `ZodType` as `out`, so an EXTRA field here is still assignable and slips
 * through. That direction is the dangerous one (an excerpt field bolted onto
 * the finding), so the spec closes it twice over: a bidirectional compile-time
 * equality, and a runtime assertion that a parsed finding has exactly two keys.
 *
 * So the write gate and this report cannot disagree about what a key is —
 * they call one function and return its one result.
 *
 * ## Why there is no excerpt, sample, or matched-text field, ever
 *
 * `kind` is one of two literals and `base64Wrapped` is a boolean. Neither is
 * derived from the value's CONTENT, so no part of a key can ride out on this
 * object — the same property `vibeEnvVarSecretMaterialMessage` already holds
 * for the refusal text, asserted the same way in the spec.
 *
 * That is the whole point of the flag. An operator, a script or a console can
 * answer "which variables hold key material?" by reading `name`, `scope` and
 * this object, and never touching `value`.
 */
export const VibeEnvVarSecretMaterialFindingSchema = z.object({
  kind: z.enum(VIBE_ENV_VAR_SECRET_MATERIAL_KINDS),
  base64Wrapped: z.boolean()
}) satisfies z.ZodType<VibeEnvVarSecretMaterialFinding>;

/**
 * The finding as a READER parses it off a response: a key-material kind a newer
 * backend detects reads as the server's own word instead of failing the whole
 * env-var list. Still two fields, neither derived from the value's content.
 */
export const VibeEnvVarSecretMaterialFindingReadSchema =
  VibeEnvVarSecretMaterialFindingSchema.extend({
    kind: enumReadSchema(VIBE_ENV_VAR_SECRET_MATERIAL_KINDS)
  });

export const VibeAppEnvVarSchema = z.object({
  id: z.string().uuid(),
  vibeAppId: z.string().uuid(),
  organizationId: z.string(),
  name: VibeEnvVarNameSchema,
  value: VibeEnvVarValueSchema,
  /**
   * What the write gate's predicate makes of `value`, or `null` for ordinary
   * configuration. Required rather than optional: an omitted field would read
   * as "clean" and is indistinguishable from a caller that never ran the
   * check, which is the exact ambiguity this exists to remove.
   *
   * Always `null` on the upsert response — that path is gated, so a value that
   * would flag cannot have been stored by it. Rows that predate the gate are
   * why the LIST response needs this.
   */
  secretMaterial: VibeEnvVarSecretMaterialFindingReadSchema.nullable(),
  scope: VibeEnvVarScopeReadSchema,
  createdByUserId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type VibeAppEnvVarDto = z.infer<typeof VibeAppEnvVarSchema>;

// ----------------------------------------------------------------------------
// Upsert env var
// ----------------------------------------------------------------------------

export const UpsertVibeAppEnvVarPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const UpsertVibeAppEnvVarBodySchema = z.object({
  name: VibeEnvVarNameSchema,
  value: VibeEnvVarSettableValueSchema,
  /// Defaults to ALL when omitted — most env vars are environment-
  /// agnostic, so the most common case takes no explicit scope.
  scope: VibeEnvVarScopeSchema.optional()
});

export const UpsertVibeAppEnvVarResponseSchema = z.object({
  envVar: VibeAppEnvVarSchema
});

// ----------------------------------------------------------------------------
// List env vars
// ----------------------------------------------------------------------------

export const ListVibeAppEnvVarsPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const ListVibeAppEnvVarsResponseSchema = z.object({
  envVars: z.array(VibeAppEnvVarSchema)
});

// ----------------------------------------------------------------------------
// Delete env var
// ----------------------------------------------------------------------------

export const DeleteVibeAppEnvVarPathVarsSchema = z.object({
  appId: z.string().uuid(),
  envVarId: z.string().uuid()
});

export const DeleteVibeAppEnvVarResponseSchema = z.object({
  deletedId: z.string().uuid()
});

// ----------------------------------------------------------------------------
// Bulk delete env vars
// ----------------------------------------------------------------------------

/// One request, one `deleteMany`. Capped well above any real env table
/// (the console's own bulk-select bar renders against a page of rows) so a
/// malformed or scripted request can't hand the DB an unbounded IN list.
export const VIBE_ENV_VAR_BULK_DELETE_MAX_IDS = 200;

export const BulkDeleteVibeAppEnvVarsPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const BulkDeleteVibeAppEnvVarsBodySchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(VIBE_ENV_VAR_BULK_DELETE_MAX_IDS)
});

/// `deletedCount` rather than an echoed id list: a `deleteMany` scoped to
/// (vibeAppId, organizationId) silently excludes any id that doesn't match
/// (wrong app, wrong org, already gone) rather than erroring — mirrors the
/// single-delete's "count=0 in scope" semantics, just batched. The console
/// refetches the list either way, so a partial match self-heals on screen.
export const BulkDeleteVibeAppEnvVarsResponseSchema = z.object({
  deletedCount: z.number().int().nonnegative()
});
